import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-chart-line-up',
    title: '1. Salesforce Sales Cloud Implementation',
    desc: 'Streamline lead management, pipeline tracking, revenue forecasting, and opportunity scoring to empower sales teams to close deals faster.',
  },
  {
    icon: 'fa-solid fa-headset',
    title: '2. Salesforce Service Cloud & Omni-Channel Support',
    desc: 'Transform customer support with automated case management, agent workspaces, AI-driven chatbots, and field service management.',
  },
  {
    icon: 'fa-solid fa-bullhorn',
    title: '3. Salesforce Marketing Cloud & Account Engagement',
    desc: 'Deliver targeted cross-channel marketing campaigns, automated customer journeys, and data-driven B2B lead nurturing (Pardot).',
  },
  {
    icon: 'fa-solid fa-cart-shopping',
    title: '4. Salesforce Commerce Cloud (B2B & B2C)',
    desc: 'Build high-converting, personalized e-commerce storefronts with unified inventory management and seamless checkout experiences.',
  },
  {
    icon: 'fa-solid fa-plug-circle-bolt',
    title: '5. MuleSoft Integration & API Management',
    desc: 'Connect Salesforce seamlessly to enterprise ERPs (SAP, Oracle), custom backends, and third-party databases via MuleSoft API pipelines.',
  },
  {
    icon: 'fa-solid fa-wand-magic-sparkles',
    title: '6. Salesforce Agentforce & Einstein AI',
    desc: 'Embed predictive AI, natural language interaction, and autonomous AI agents into daily CRM workflows to automate routine operations.',
  },
  {
    icon: 'fa-solid fa-calculator',
    title: '7. Salesforce CPQ & Billing Optimization',
    desc: 'Automate complex product catalog configurations, pricing rules, contract generation, and recurring subscription billing schedules.',
  },
  {
    icon: 'fa-solid fa-chart-pie',
    title: '8. CRM Analytics & Tableau Integration',
    desc: 'Convert CRM data into actionable intelligence with dynamic, real-time dashboards and predictive business insights.',
  },
  {
    icon: 'fa-solid fa-code-merge',
    title: '9. Custom Apex, LWC & Industry Clouds',
    desc: 'Develop custom Lightning Web Components (LWC) and deploy tailored Salesforce Industry Clouds (Financial Services, Health, Manufacturing).',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '10. Salesforce Managed Services & Health Audits',
    desc: 'Maintain platform health, optimize user security profiles, execute org cleanups, and ensure continuous post-implementation support.',
  },
];

const whyChooseUs = [
  'Certified Salesforce Architects and Industry Cloud Specialists',
  'Deep expertise in Apex, Lightning Web Components (LWC), and Flow Automation',
  'Seamless integrations using MuleSoft and REST/SOAP APIs',
  'Enterprise Data Cloud and Einstein AI deployment strategies',
  'Strict security, role hierarchy, and compliance governance',
  'Accelerated implementation frameworks that reduce time-to-value',
  'High end-user adoption through structured change management',
  'Proven track record in multi-org consolidation and data migration',
  'Tailored B2B and B2C omnichannel customer journey design',
  'Flexible 24/7 Managed Services and continuous org optimization',
];

const processSteps = [
  { step: '01', title: 'Org Audit & Discovery', desc: 'Assess current sales/service processes, tech architecture, data quality, and business goals.' },
  { step: '02', title: 'Solution Architecture', desc: 'Design scalable data models, security roles, flow automations, and integration blueprints.' },
  { step: '03', title: 'Configuration & Custom Coding', desc: 'Build workflows, configure standard/custom objects, and develop custom LWC components.' },
  { step: '04', title: 'Data Migration & Integration', desc: 'Cleanse, map, and migrate legacy CRM records while establishing active MuleSoft APIs.' },
  { step: '05', title: 'Testing & User Enablement', desc: 'Conduct UAT, end-to-end security checks, and interactive user training sessions.' },
  { step: '06', title: 'Go-Live & Managed Support', desc: 'Execute smooth deployment, monitor user adoption, and provide ongoing platform enhancements.' },
];

const techStack = [
  { category: 'Core Clouds', items: ['Sales Cloud', 'Service Cloud', 'Marketing Cloud', 'Commerce Cloud', 'Experience Cloud'] },
  { category: 'AI & Analytics', items: ['Agentforce', 'Einstein AI', 'CRM Analytics', 'Tableau Cloud', 'Data Cloud'] },
  { category: 'Development & Tools', items: ['Lightning Web Components (LWC)', 'Apex', 'Salesforce Flow', 'MuleSoft', 'Copado'] },
  { category: 'Industry Clouds', items: ['Financial Services Cloud', 'Health Cloud', 'Manufacturing Cloud', 'Nonprofit Cloud'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const SalesforcePlatforms = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Technology Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Salesforce <span className="gradient-text-cyan">Platforms</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we empower organizations to maximize their CRM investment. From Sales and Service Cloud rollouts to custom LWC development and MuleSoft integrations, we build connected, AI-driven customer ecosystems.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Elevate Your Salesforce Ecosystem &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Salesforce Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Salesforce Technology Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Salesforce Implementation Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Transform Your Customer Relationships with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Streamline sales cycles, elevate customer service, and unlock intelligent AI automation with tailored Salesforce solutions.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Salesforce Architect &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SalesforcePlatforms;